import { FunctionComponent, memo, useEffect } from "react";
import { IProps } from "./interaction-modal-content.types";
import { useInteractionDetails } from "hooks-for/interactions/use-interaction-details";
import { SpinnerUntil } from "@edgetier/components";
import InteractionProvider from "../interaction-provider";
import { ModalContent, ModalFooter, ModalHeader } from "@edgetier/modal";
import InteractionTitle from "../interaction-title";
import InteractionOptionsMenu from "../interaction-options-menu";
import InteractionContent from "../interaction-content";
import { NumericValue } from "@edgetier/client-components";
import "./interaction-modal-content.scss";
import { trackUser } from "utilities/track-user";

/**
 * The content of an interaction to be displayed in a modal.
 * @param props.areWritingIssuesVisible Are writing issues visible in the interaction messages.
 * @param props.interactionId           The ID of the interaction being viewed.
 * @param props.interactionIndex        Index position of the current interaction
 * @param props.interactionsCount       Total count of all interactions
 * @param props.highlightedMessageId    The message ID which will be highlighted
 */
const InteractionModalContent: FunctionComponent<IProps> = ({
    areWritingIssuesVisible,
    interactionId,
    interactionIndex,
    interactionsCount,
    highlightedMessageId,
}) => {
    useEffect(() => {
        trackUser("View Interaction", { interactionId });
    }, [interactionId]);

    const { data: interaction, isLoading: isInteractionLoading } = useInteractionDetails(interactionId);

    return (
        <div className="interaction-modal-content">
            <SpinnerUntil data={interaction} isReady={!isInteractionLoading}>
                {typeof interaction === "undefined" ? (
                    "Error"
                ) : (
                    <InteractionProvider
                        areWritingIssuesVisible={areWritingIssuesVisible}
                        agentId={interaction.agentId}
                    >
                        <ModalHeader>
                            <div className="interaction-modal-content__header">
                                <InteractionTitle
                                    contentCleaned={interaction.contentCleaned}
                                    interactionType={interaction.interactionType}
                                    subject={interaction.subject}
                                    interactionId={interaction.interactionId}
                                    languageIsoCode={interaction.languageIsoCode}
                                />

                                <InteractionOptionsMenu
                                    interactionId={interaction.interactionId}
                                    interactionUrl={interaction.url}
                                />
                            </div>
                        </ModalHeader>
                        <ModalContent>
                            <div className="interaction-modal-content__content">
                                <InteractionContent
                                    agentId={interaction.agentId}
                                    interactionId={interaction.interactionId}
                                    interactionType={interaction.interactionType}
                                    tags={interaction.tags}
                                    highlightedMessageId={highlightedMessageId}
                                />
                            </div>
                        </ModalContent>
                        <ModalFooter>
                            {typeof interactionIndex === "number" && interactionIndex > -1 && (
                                <div className="interaction-modal-content__interaction-position">
                                    {interactionIndex + 1} of <NumericValue value={interactionsCount} /> interactions
                                </div>
                            )}
                        </ModalFooter>
                    </InteractionProvider>
                )}
            </SpinnerUntil>
        </div>
    );
};

export default memo(InteractionModalContent);
